import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Tag,
  Tabs,
  Button,
  Space,
  Typography,
  Tooltip,
} from 'antd';
import {
  EyeOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { pendingInvoicesApi } from '../services/api';

const { Title } = Typography;

interface PendingInvoiceCounts {
  PENDING: number;
  IN_REVIEW: number;
  SENT: number;
  FAILED: number;
}

const PendingInvoicesList: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('PENDING');

  const { data: invoices, isLoading, refetch } = useQuery({
    queryKey: ['pending-invoices', activeTab],
    queryFn: async () => {
      const response = await pendingInvoicesApi.list(activeTab);
      return Array.isArray(response) ? response : (response as any).invoices || [];
    },
  });

  const { data: counts } = useQuery<PendingInvoiceCounts>({
    queryKey: ['pending-invoices-counts'],
    queryFn: async () => {
      return await pendingInvoicesApi.getCounts() as unknown as PendingInvoiceCounts;
    },
    refetchInterval: 60000,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'gold';
      case 'IN_REVIEW':
        return 'blue';
      case 'SENT':
        return 'green';
      case 'FAILED':
        return 'red';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Shipment Ref',
      dataIndex: 'shipmentRef',
      key: 'shipmentRef',
      render: (text: string) => (
        <Tag color="geekblue">{text}</Tag>
      ),
    },
    {
      title: 'Contact',
      dataIndex: 'contactName',
      key: 'contactName',
    },
    {
      title: 'Date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => (
        <span style={{ fontFamily: 'monospace' }}>
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(amount || 0)}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Tooltip title="View & Edit">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/pending-invoices/${record.id}`);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const items = [
    {
      key: 'PENDING',
      label: (
        <span>
          Pending
          {(counts?.PENDING ?? 0) > 0 && (
            <Tag color="gold" style={{ marginLeft: 8, borderRadius: 10 }}>
              {counts?.PENDING}
            </Tag>
          )}
        </span>
      ),
    },
    {
      key: 'IN_REVIEW',
      label: (
        <span>
          In Review
          {(counts?.IN_REVIEW ?? 0) > 0 && (
            <Tag color="blue" style={{ marginLeft: 8, borderRadius: 10 }}>
              {counts?.IN_REVIEW}
            </Tag>
          )}
        </span>
      ),
    },
    {
      key: 'SENT',
      label: 'Sent to Xero',
    },
    {
      key: 'FAILED',
      label: (
        <span>
          Failed
          {(counts?.FAILED ?? 0) > 0 && (
            <Tag color="red" style={{ marginLeft: 8, borderRadius: 10 }}>
              {counts?.FAILED}
            </Tag>
          )}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>Pending Invoices</Title>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </div>

      <Card
        bodyStyle={{ padding: 0 }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={items}
          tabBarStyle={{ padding: '0 24px' }}
        />
        
        <Table
          dataSource={invoices || []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20 }}
          onRow={(record) => ({
            onClick: () => navigate(`/pending-invoices/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </div>
  );
};

export default PendingInvoicesList;
